import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api from '../../api/axiosInstance2';
import { getToken } from '../../utils/secureStorage';
import { FiRefreshCw, FiSave, FiLock, FiUser, FiCheck } from 'react-icons/fi';

const Profile = () => {
    const navigate = useNavigate();
    const [setBreadcrumb, setBreadcrumbLink] = useOutletContext();
    const email = getToken('email');

    // Avatar State
    const [avatarSeed, setAvatarSeed] = useState(localStorage.getItem('user_avatar_seed') || 'aditya');
    const [tempAvatarSeed, setTempAvatarSeed] = useState(avatarSeed);
    const [isSaved, setIsSaved] = useState(false);

    // Password State
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [proceeding, setProceeding] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    useEffect(() => {
        setBreadcrumb(['Profile']);
        setBreadcrumbLink(['/profile']);
    }, [setBreadcrumb, setBreadcrumbLink]);

    // --- Avatar Logic ---
    const generateNewAvatar = () => {
        const randomSeed = Math.random().toString(36).substring(7);
        setTempAvatarSeed(randomSeed);
        setIsSaved(false);
    };

    const saveAvatar = () => {
        localStorage.setItem('user_avatar_seed', tempAvatarSeed);
        setAvatarSeed(tempAvatarSeed);
        setIsSaved(true);
        // Dispatch a custom event so Navbar can listen and update immediately
        window.dispatchEvent(new Event('storage'));
        setTimeout(() => setIsSaved(false), 2000);
    };

    const getAvatarUrl = (seed) => {
        return `https://api.dicebear.com/7.x/notionists/svg?seed=${seed}&backgroundColor=b6e3f4,c0aede,d1d4f9`;
    };

    // --- Password Logic ---
    const changePassword = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMsg('');

        if (!password || !currentPassword || !confirmPassword) {
            setError('All fields are required.');
            return;
        }
        if (password !== confirmPassword) {
            setError('New Password and Confirm Password do not match.');
            return;
        }
        if (password === currentPassword) {
            setError('New Password cannot be the same as Current Password.');
            return;
        }

        try {
            setProceeding(true);
            const res = await api.put(`/auth/change-password`, {
                oldPassword: currentPassword,
                newPassword: password,
                confirmPassword: confirmPassword
            });

            if (!res.data.error) {
                setSuccessMsg('Password changed successfully! Logging out...');
                setPassword('');
                setConfirmPassword('');
                setCurrentPassword('');
                setTimeout(() => navigate('/logout'), 2000);
            } else {
                setError(res.data.message);
            }
        } catch (err) {
            setError(err.message === "Network Error" ? 'No Internet Connection' : 'Failed to change password.');
            console.error(err);
        } finally {
            setProceeding(false);
        }
    };

    return (
        <div className="p-4 sm:p-6 max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                {/* --- Identity Card (Left Column) --- */}
                <div className="lg:col-span-1">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 flex flex-col items-center text-center h-full">
                        <div className="relative group">
                            <div className="w-40 h-40 rounded-full overflow-hidden border-4 border-white shadow-lg bg-gray-100 mb-4 transition-transform transform hover:scale-105">
                                <img
                                    src={getAvatarUrl(tempAvatarSeed)}
                                    alt="User Avatar"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <button
                                onClick={generateNewAvatar}
                                className="absolute bottom-2 right-2 p-2 bg-white text-gray-700 rounded-full shadow-md hover:bg-gray-50 transition-colors"
                                title="Shuffle Avatar"
                            >
                                <FiRefreshCw size={18} />
                            </button>
                        </div>

                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">Hello!</h2>
                        <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">{email}</p>

                        <button
                            onClick={saveAvatar}
                            disabled={isSaved || tempAvatarSeed === avatarSeed}
                            className={`flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-xl font-medium transition-all ${isSaved
                                    ? 'bg-green-100 text-green-700 border border-green-200'
                                    : 'bg-gray-900 text-white hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100'
                                }`}
                        >
                            {isSaved ? <><FiCheck /> Saved</> : <><FiSave /> Save New Look</>}
                        </button>

                        <p className="mt-4 text-xs text-gray-400">
                            This avatar is stored locally on your device.
                        </p>
                    </div>
                </div>

                {/* --- Security Settings (Right Column) --- */}
                <div className="lg:col-span-2">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 h-full">
                        <div className="flex items-center gap-3 mb-6 border-b border-gray-100 dark:border-gray-700 pb-4">
                            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                                <FiLock size={24} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Security Settings</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Update your password to keep your account safe.</p>
                            </div>
                        </div>

                        {error && (
                            <div className="p-4 mb-6 text-sm text-red-800 rounded-xl bg-red-50 border border-red-100 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800">
                                {error}
                            </div>
                        )}

                        {successMsg && (
                            <div className="p-4 mb-6 text-sm text-green-800 rounded-xl bg-green-50 border border-green-100 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800">
                                {successMsg}
                            </div>
                        )}

                        <form onSubmit={changePassword} className="space-y-5">
                            <div>
                                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Current Password</label>
                                <input
                                    type="password"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-xl focus:ring-blue-500 focus:border-blue-500 block w-full p-3 transition-colors dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                                    placeholder="••••••••"
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">New Password</label>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-xl focus:ring-blue-500 focus:border-blue-500 block w-full p-3 transition-colors dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                                        placeholder="••••••••"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Confirm Password</label>
                                    <input
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-xl focus:ring-blue-500 focus:border-blue-500 block w-full p-3 transition-colors dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                                        placeholder="••••••••"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="pt-4">
                                <button
                                    type="submit"
                                    disabled={proceeding}
                                    className="text-white bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 font-medium rounded-xl text-sm px-6 py-3 text-center transition-all disabled:opacity-70 disabled:cursor-not-allowed dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 flex items-center gap-2"
                                >
                                    {proceeding ? (
                                        <>
                                            <svg aria-hidden="true" className="w-4 h-4 animate-spin fill-white" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67227 50 9.67227C27.4013 9.67227 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                                                <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.723 75.2124 7.55316C69.5422 4.38329 63.2754 2.51526 56.7335 2.08404C51.7661 1.74012 46.7787 2.38589 42.0637 3.98731C39.5466 4.81519 38.0842 7.343 38.7213 9.76854C39.3585 12.1941 41.8408 13.6585 44.3491 12.8944C47.9682 11.8201 51.7359 11.5292 55.4318 11.9447C60.3374 12.5079 65.0716 14.2405 69.2762 17.0357C73.4807 19.8308 77.0427 23.6187 79.6469 28.1491C81.9113 32.0014 83.3647 36.2565 83.9235 40.6341C84.336 43.3453 86.7423 45.0712 89.1677 44.4341Z" fill="currentFill" />
                                            </svg>
                                            Updating...
                                        </>
                                    ) : 'Update Password'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;
